import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { setCurrentConversation } from '../store/reducers/messages.reducer';
import { useMessaging } from '../hooks/useMessaging';
import { ChatWindow } from '../components/chat/ChatWindow';
import { getFamilyMembers } from '../api/userApi';
import { User } from '../types';

export const MessagesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { loginUser } = useAppSelector((state) => state.user);
  const { currentConversation, onlineUsers } = useAppSelector((state) => state.messages);
  const { conversations, messages, sendMessage, loadMessages, markAsRead, isUserOnline, isConnected, deleteConversation } = useMessaging();
  const [familyMembers, setFamilyMembers] = useState<User[]>([]);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [newChatSearch, setNewChatSearch] = useState('');
  const [swipedConversation, setSwipedConversation] = useState<string | null>(null);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null);

  // Convert onlineUsers array to object for easy lookup
  const onlineStatus = React.useMemo(() => {
    const status: { [key: string]: boolean } = {};
    onlineUsers.forEach(userId => { status[userId] = true; });
    return status;
  }, [onlineUsers]);

  useEffect(() => {
    const loadFamily = async () => {
      try {
        const response = await getFamilyMembers();
        if (response.data?.success && response.data.data?.members) {
          setFamilyMembers(response.data.data.members);
        }
      } catch (error) {
        console.error('Failed to load family members:', error);
      }
    };

    loadFamily();
  }, []);


  const handleSelectConversation = async (userId: string) => {
    dispatch(setCurrentConversation(userId));
    await loadMessages(userId);
    await markAsRead(userId);
  };

  const handleSendMessage = async (content: string, mediaData?: { mediaType: string; mediaUrl: string; thumbnailUrl?: string }) => {
    if (!currentConversation) return;
    await sendMessage(currentConversation, content, mediaData);
  };

  const handleCloseChat = () => {
    dispatch(setCurrentConversation(null));
  };

  const handleDeleteConversation = async (userId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation? It will be moved to trash.')) return;

    try {
      await deleteConversation(userId);
      setSwipedConversation(null);
      if (currentConversation === userId) {
        handleCloseChat();
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      alert('Failed to delete conversation. Please try again.');
    }
  };

  const handleTouchStart = (userId: string, e: React.TouchEvent) => {
    setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    setTouchEnd(null);
  };

  const handleTouchMove = (userId: string, e: React.TouchEvent) => {
    setTouchEnd({ x: e.touches[0].clientX, y: e.touches[0].clientY });
  };

  const handleTouchEnd = (userId: string) => {
    if (!touchStart || !touchEnd) return;

    const deltaX = touchStart.x - touchEnd.x;
    const deltaY = Math.abs(touchStart.y - touchEnd.y);

    // Swipe left to reveal delete (deltaX > 50 = swipe left, deltaY < 30 = horizontal swipe)
    if (deltaX > 50 && deltaY < 30) {
      setSwipedConversation(userId);
    } else if (deltaX < -50 && deltaY < 30) {
      // Swipe right to hide delete
      setSwipedConversation(null);
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  const currentRecipient = familyMembers.find(m => String(m.id) === currentConversation);

  return (
    <div style={{
      display: 'flex',
      height: 'calc(100vh - 64px - 80px)', // 64px AppBar + 80px bottom nav
      overflow: 'hidden'
    }}>
      {/* Conversations List */}
      <div 
      style={{
        width: currentConversation ? '0' : '100%',
        maxWidth: '400px',
        backgroundColor: 'var(--background-paper)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'width 0.3s ease'
      }} className={currentConversation ? 'd-none d-md-block' : ''}>
        <style>{`
          @media (max-width: 768px) {
            .messages-list-column {
              max-width: 100vw !important;
            }
          }
        `}</style>
        <div className="messages-list-column" style={{ width: '100%' }}>
        <div className="card" style={{ flex: 1, borderRadius: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div className="card-content" style={{ padding: '16px', borderBottom: '1px solid var(--border-color)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 className="h5 mb-0">Messages</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  className="btn btn-sm btn-contained"
                  onClick={() => setShowNewChatModal(true)}
                  style={{ padding: '4px 8px' }}
                >
                  <span className="material-icons" style={{ fontSize: '18px' }}>add</span>
                </button>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  color: isConnected ? 'var(--success-color)' : 'var(--error-color)'
                }}>
                  <span style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: isConnected ? 'var(--success-color)' : 'var(--error-color)'
                  }} />
                  {isConnected ? 'Connected' : 'Offline'}
                </div>
              </div>
            </div>
          </div>

          {/* Scrollable conversations area */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
            {conversations.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text-secondary)', marginTop: '32px' }}>
                <span className="material-icons" style={{ fontSize: '48px', opacity: 0.3 }}>
                  forum
                </span>
                <div style={{ marginTop: '8px' }}>No conversations yet</div>
                <div style={{ fontSize: '14px', marginTop: '4px' }}>
                  Open a family member's profile to start chatting
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {conversations.map((conv) => {
                  const member = familyMembers.find(m => String(m.id) === conv.userId);
                  if (!member) return null;

                  const displayName = member.displayName || member.display_name || member.email;
                  const avatarUrl = member.photoURL || member.photo_url;
                  const isOnline = onlineStatus[conv.userId] || false;

                  return (
                    <div
                      key={conv.userId}
                      style={{ position: 'relative', overflow: 'hidden' }}
                      onTouchStart={(e) => handleTouchStart(conv.userId, e)}
                      onTouchMove={(e) => handleTouchMove(conv.userId, e)}
                      onTouchEnd={() => handleTouchEnd(conv.userId)}
                    >
                      <div
                        className="card"
                        onClick={() => handleSelectConversation(conv.userId)}
                        style={{
                          cursor: 'pointer',
                          border: currentConversation === conv.userId ? '2px solid var(--primary-color)' : '1px solid var(--border-color)',
                          transform: swipedConversation === conv.userId ? 'translateX(-80px)' : 'translateX(0)',
                          transition: 'transform 0.3s ease',
                          position: 'relative'
                        }}
                      >
                        <div className="card-content" style={{ padding: '12px' }}>
                          {/* Delete button - visible on desktop hover */}
                          <button
                            className="icon-btn"
                            onClick={(e) => handleDeleteConversation(conv.userId, e)}
                            title="Delete conversation"
                            style={{
                              position: 'absolute',
                              top: '8px',
                              right: '8px',
                              color: 'var(--error-color)',
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              zIndex: 10
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '0'}
                            onFocus={(e) => e.currentTarget.style.opacity = '1'}
                            onBlur={(e) => e.currentTarget.style.opacity = '0'}
                          >
                            <span className="material-icons" style={{ fontSize: '20px' }}>delete</span>
                          </button>
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <div style={{ position: 'relative' }}>
                              {avatarUrl ? (
                                <img
                                  src={avatarUrl}
                                  alt={displayName}
                                  style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover' }}
                                />
                              ) : (
                                <div style={{
                                  width: '48px',
                                  height: '48px',
                                  borderRadius: '50%',
                                  background: 'var(--primary-color)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}>
                                  {displayName.charAt(0).toUpperCase()}
                                </div>
                              )}
                              {isOnline && (
                                <span style={{
                                  position: 'absolute',
                                  bottom: '2px',
                                  right: '2px',
                                  width: '12px',
                                  height: '12px',
                                  borderRadius: '50%',
                                  background: 'var(--success-color)',
                                  border: '2px solid var(--background-paper)'
                                }} />
                              )}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                                {displayName}
                              </div>
                              {conv.lastMessage && (
                                <div style={{
                                  fontSize: '14px',
                                  color: 'var(--text-secondary)',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {conv.lastMessage.content}
                                </div>
                              )}
                            </div>
                            {conv.unreadCount > 0 && (
                              <div style={{
                                background: 'var(--primary-color)',
                                color: 'white',
                                borderRadius: '50%',
                                minWidth: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                fontWeight: 'bold'
                              }}>
                                {conv.unreadCount}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Delete button revealed on swipe */}
                      {swipedConversation === conv.userId && (
                        <div
                          onClick={(e) => handleDeleteConversation(conv.userId, e)}
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '80px',
                            background: 'var(--error-color)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        >
                          <span className="material-icons">delete</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Chat Window */}
      {currentConversation && currentRecipient ? (
        <div style={{ flex: 1, background: 'var(--background-default)' }}>
          <ChatWindow
            recipient={currentRecipient}
            currentUserId={String(loginUser?.id)}
            messages={messages}
            onSendMessage={handleSendMessage}
            onClose={handleCloseChat}
            isOnline={onlineStatus[currentConversation] || false}
          />
        </div>
      ) : (
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-secondary)',
          flexDirection: 'column',
          gap: '16px'
        }} className="d-none d-md-flex">
          <span className="material-icons" style={{ fontSize: '64px', opacity: 0.3 }}>
            chat_bubble_outline
          </span>
          <div>Select a conversation to start messaging</div>
        </div>
      )}

      {/* New Chat Modal */}
      {showNewChatModal && (
        <>
          <div
            className="position-fixed"
            style={{
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              zIndex: 2000,
            }}
            onClick={() => setShowNewChatModal(false)}
          />
          <div
            className="paper auth-form-container"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2001,
              padding: '24px',
              maxHeight: '70vh',
              overflowY: 'auto',
              minWidth: '300px',
              maxWidth: '500px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '1.5em', color: 'white' }}>Start New Chat</h2>
              <button className="icon-btn" onClick={() => setShowNewChatModal(false)} style={{ color: 'white' }}>
                <span className="material-icons">close</span>
              </button>
            </div>

            {/* Search Field */}
            <div className="textfield" style={{ marginBottom: '16px' }}>
              <div className="textfield-root">
                <input
                  className="textfield-input"
                  type="text"
                  placeholder="Search family members..."
                  value={newChatSearch}
                  onChange={(e) => setNewChatSearch(e.target.value)}
                />
                <div className="input-adornment input-adornment-end">
                  <span className="material-icons">search</span>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {familyMembers
                .filter(member => String(member.id) !== String(loginUser?.id))
                .filter(member => {
                  if (!newChatSearch) return true;
                  const searchLower = newChatSearch.toLowerCase();
                  const displayName = (member.displayName || member.display_name || '').toLowerCase();
                  const email = (member.email || '').toLowerCase();
                  const firstName = (member.firstName || member.first_name || '').toLowerCase();
                  const lastName = (member.lastName || member.last_name || '').toLowerCase();
                  return displayName.includes(searchLower) ||
                         email.includes(searchLower) ||
                         firstName.includes(searchLower) ||
                         lastName.includes(searchLower);
                })
                .map((member) => {
                  const displayName = member.displayName || member.display_name || member.email;
                  const avatarUrl = member.photoURL || member.photo_url;

                  return (
                    <div
                      key={member.id}
                      className="card"
                      onClick={() => {
                        handleSelectConversation(String(member.id));
                        setShowNewChatModal(false);
                      }}
                      style={{
                        cursor: 'pointer',
                        transition: 'border-color 0.2s'
                      }}
                    >
                      <div className="card-content" style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                          {avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt={displayName}
                              style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover' }}
                            />
                          ) : (
                            <div style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '50%',
                              background: 'var(--primary-color)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '20px'
                            }}>
                              {displayName.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                              {displayName}
                            </div>
                            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                              {member.email}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
